import { useRef, useState } from "react";
import { Button, FormInstance, Form, Input, message, Checkbox, Typography, Select } from "antd";

import { emailPassSignup } from "api/consumerLogin";
import { OtpForm } from "../Otp/Otp";
import { verifyOTP } from "api/consumerLogin";
import AuthScreenContainer from "containers/AuthScreenContainer";
import { Link, useNavigate } from "react-router-dom";
import { isValidEmail, STRONG_PASSWORD_PATTERN } from "utils/validation";
import { useLogout } from "@refinedev/core";
import { VALIDATION } from "constants/strings";

export const RegistrationForm = () => {
  const [loading, setLoading] = useState(false);
  const [showOtp, setShowOtp] = useState(false);
  const { mutate: logout } = useLogout();
  const [email, setEmail] = useState("");
  const navigate = useNavigate();

  const formRef = useRef<FormInstance>(null);

  const handleSignUp = async () => {
    const { password, confirmPassword, email, firstName, lastName, organization, agreeConditions } =
      formRef.current?.getFieldsValue();
    setEmail(email);

    if (!email || !password || !confirmPassword || !firstName || !lastName || !organization) {
      message.error("Please fill all the fields");
      return;
    }

    // if email is not valid
    if (!isValidEmail(email)) {
      message.error("Please enter a valid email");
      return;
    }

    if (password !== confirmPassword) {
      message.error("Passwords do not match");
      return;
    }

    if (!agreeConditions) {
      message.error("You must accept the Terms & Conditions and End User Agreement");
      return;
    }

    setLoading(true);

    const response = await emailPassSignup(formRef.current?.getFieldsValue());
    setLoading(false);
    if (response) {
      setLoading(false);
      setShowOtp(true);
    }
  };

  const next = (data: any) => {
    if (data.approved) {
      navigate("/onboarding", {
        state: { email: data.email }
      });
    } else {
      logout();
      navigate("/account-pending", {
        replace: true,
        state: { email: data.email }
      });
    }
  };

  if (showOtp) {
    return (
      <OtpForm email={email} next={next} verify={verifyOTP} />
    );
  }

  return (
    <AuthScreenContainer
      heading="Sign up"
      footerText="Already have an account?"
      footerLinkText={"Sign in"}
      footerLink="/login">
      <Form
        scrollToFirstError
        requiredMark={false}
        onFinish={handleSignUp}
        ref={formRef}
        layout="vertical">
        <Form.Item
          style={{ padding: 0, marginTop: 12, marginBottom: 12 }}
          name="firstName"
          rules={[
            {
              required: true,
              whitespace: true,
              message: VALIDATION.fieldIsRequired
            },
            {
              min: 2,
              whitespace: true,
              transform: (value: string) => value.trim(),
              message: VALIDATION.getMinCharacterValidationMessage("First name", 2)
            },
            {
              max: 255,
              message: VALIDATION.getMaxCharacterValidationMessage("First name", 256)
            }
          ]}
          label={"First name"}>
          <Input type="text" />
        </Form.Item>
        <Form.Item
          style={{ padding: 0, marginTop: 12, marginBottom: 12 }}
          name="lastName"
          label={"Last name"}
          rules={[
            {
              required: true,
              whitespace: true,
              message: VALIDATION.fieldIsRequired
            },
            {
              min: 2,
              whitespace: true,
              transform: (value: string) => value.trim(),
              message: VALIDATION.getMinCharacterValidationMessage("Last name", 2)
            },
            {
              max: 255,
              message: VALIDATION.getMaxCharacterValidationMessage("Last name", 256)
            }
          ]}>
          <Input type="text" />
        </Form.Item>
        <Form.Item
          style={{ padding: 0, marginTop: 12, marginBottom: 12 }}
          name="organization"
          label={"Organisation name"}
          rules={[
            {
              required: true,
              whitespace: true,
              message: VALIDATION.fieldIsRequired
            },
            {
              min: 2,
              whitespace: true,
              transform: (value: string) => value.trim(),
              message: VALIDATION.getMinCharacterValidationMessage("Organisation name", 2)
            },
            {
              max: 255,
              message: VALIDATION.getMaxCharacterValidationMessage("Organisation name", 256)
            }
          ]}>
          <Input type="text" />
        </Form.Item>
        <Form.Item
          style={{ padding: 0, marginTop: 12, marginBottom: 12 }}
          name="email"
          label={"Email"}
          rules={[
            {
              required: true,
              whitespace: true,
              message: VALIDATION.fieldIsRequired
            },
            {
              type: "email",
              message: VALIDATION.invalidEmail
            }
          ]}>
          <Input type="email" />
        </Form.Item>
        <Form.Item
          style={{ padding: 0, marginTop: 12, marginBottom: 12 }}
          name="password"
          label={"Password"}
          normalize={(value: string) => value.trim()}
          rules={[
            {
              required: true,
              pattern: STRONG_PASSWORD_PATTERN,
              message: VALIDATION.passwordValidation
            }
          ]}>
          <Input.Password type="password" />
        </Form.Item>
        <Form.Item
          style={{ padding: 0, marginTop: 12, marginBottom: 12 }}
          name="confirmPassword"
          label={"Confirm password"}
          dependencies={["password"]}
          rules={[
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value) {
                  return Promise.reject(VALIDATION.fieldIsRequired);
                }

                if (getFieldValue("password") !== value) {
                  return Promise.reject(VALIDATION.confirmPasswordValidation);
                }

                return Promise.resolve();
              }
            })
          ]}>
          <Input.Password type="password" />
        </Form.Item>
        <Form.Item
          style={{ padding: 0, marginTop: 12, marginBottom: 12 }}
          name="causeOfUse"
          label={"What is your intent of using QuiX Quantum?"}
          rules={[
            () => ({
              validator(_, value) {
                if (!value) {
                  return Promise.reject(VALIDATION.fieldIsRequired);
                }
                return Promise.resolve();
              }
            })
          ]}>
          <Select>
            <Select.Option disabled>Select cause of use</Select.Option>
            <Select.Option value="PERSONAL">Personal</Select.Option>
            <Select.Option value="PROFESSIONAL">Professional</Select.Option>
          </Select>
        </Form.Item>
        <Form.Item
          style={{ padding: 0, marginTop: 12, marginBottom: 12 }}
          colon={false}
          name="agreeConditions"
          valuePropName="checked"
          rules={[
            {
              required: true,
              message: VALIDATION.fieldIsRequired
            }
          ]}>
          <Checkbox style={{ display: "flex", alignItems: "flex-start"}}>
            <Typography>
              I agree to the &nbsp;
              <Link to="/terms-and-conditions" target="_blank">
                Terms & Conditions
              </Link>
              , &nbsp;
              <Link to="/end-user-statement" target="_blank">
                End User Agreement
              </Link>
              &nbsp; and &nbsp;
              <Link to="https://www.quixquantum.com/privacy-statement" target="_blank">
                Privacy Policy
              </Link>
            </Typography>
          </Checkbox>
        </Form.Item>
        <Form.Item
          style={{ padding: 0, marginTop: 12, marginBottom: 12 }}
          colon={false}
          name="marketingEmailPreference"
          valuePropName="checked">
          <Checkbox>
            <Typography>I would like to receive marketing and newsletter emails</Typography>
          </Checkbox>
        </Form.Item>
        <Button
          htmlType="submit"
          loading={loading}
          disabled={loading}
          style={{
            padding: "16px, 24px, 16px, 24px",
            width: "100%",
            height: "56px",
            fontSize: "20px",
            fontWeight: 600,
            lineHeight: "29px",
            borderRadius: "8px"
          }}
          onClick={(e) => {
            e.preventDefault();
            formRef.current?.submit();
          }}>
          Sign up
        </Button>
      </Form>
    </AuthScreenContainer>
  );
};
